import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const BRAND = '#214140';
const GREEN = '#1F8051';
const TEAL  = '#8BBAB8';
const LIGHT = '#DFEAF0';

const CALENDLY = 'https://calendly.com/davidusevitch/meet-with-david-nursetech-medical';

// ── Shared layout shell ──────────────────────────────────────
const Shell = ({ profile, onSignOut, children }) => {
  const badge = { pending: 'Pending Review', basic: 'Basic Access', full: 'Full Partner', admin: 'Admin' };
  const badgeColor = { pending: '#f59e0b', basic: '#1F8051', full: '#214140', admin: '#7c3aed' };
  const level = profile?.access_level || 'pending';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f9fafb' }}>
      {/* Top bar */}
      <header style={{ background: BRAND }} className="px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-white font-bold text-lg">Cordflex® Partner Portal</span>
          <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold"
            style={{ background: badgeColor[level] + '30', color: badgeColor[level] === '#f59e0b' ? '#b45309' : badgeColor[level] }}>
            {badge[level]}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-300 hidden sm:block">{profile?.full_name}</span>
          <button onClick={onSignOut}
            className="text-sm px-3 py-1.5 rounded-lg transition-all hover:bg-white/10"
            style={{ color: TEAL, border: `1px solid ${TEAL}40` }}>
            Sign Out
          </button>
        </div>
      </header>
      <div className="flex-1 px-4 py-8 max-w-5xl mx-auto w-full">
        {children}
      </div>
    </div>
  );
};

// ── Card component ───────────────────────────────────────────
const Card = ({ title, subtitle, children, locked }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm relative" style={{ border: `1px solid #e5e7eb`, opacity: locked ? 0.55 : 1 }}>
    {locked && (
      <div className="absolute top-3 right-3 flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
        style={{ background: '#f3f4f6', color: '#6b7280' }}>
        🔒 Unlocked after onboarding
      </div>
    )}
    <h3 className="font-bold text-base mb-1" style={{ color: BRAND }}>{title}</h3>
    {subtitle && <p className="text-xs text-gray-400 mb-4">{subtitle}</p>}
    {children}
  </div>
);

// ── Resource Link ────────────────────────────────────────────
const ResLink = ({ icon, label, href, locked }) => (
  <a
    href={locked ? undefined : href}
    target={href && !locked ? '_blank' : undefined}
    rel="noreferrer"
    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all"
    style={{
      background: locked ? '#f9fafb' : LIGHT,
      color: locked ? '#9ca3af' : BRAND,
      cursor: locked ? 'not-allowed' : 'pointer',
      border: `1px solid ${locked ? '#e5e7eb' : TEAL + '50'}`
    }}>
    <span>{icon}</span>
    <span className="font-medium flex-1">{label}</span>
    {locked ? <span className="text-xs opacity-60">🔒</span> : <span className="text-xs opacity-60">↗</span>}
  </a>
);

// ─────────────────────────────────────────────────────────────
// PENDING VIEW  (applied, not yet reviewed)
// ─────────────────────────────────────────────────────────────
const PendingView = ({ profile }) => (
  <div className="space-y-6">
    {/* Status banner */}
    <div className="rounded-xl p-6 text-center" style={{ background: '#fffbeb', border: '1px solid #fbbf24' }}>
      <div className="text-3xl mb-3">⏳</div>
      <h2 className="text-xl font-bold mb-2" style={{ color: '#92400e' }}>Application Under Review</h2>
      <p className="text-sm text-yellow-800 max-w-md mx-auto">
        Hi <strong>{profile?.full_name}</strong> — we've received your application and will be reviewing it shortly.
        Once approved, you'll receive an email and can access additional resources here.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Schedule a call */}
      <Card title="Schedule a Discovery Call" subtitle="Learn more about the program before we approve your access">
        <p className="text-sm text-gray-600 mb-4">
          The fastest way to move forward — book a 30-minute call with David to discuss your territory,
          network, and how Cordflex fits your line.
        </p>
        <a href={CALENDLY} target="_blank" rel="noreferrer"
          className="inline-block px-5 py-2.5 rounded-lg font-semibold text-sm text-white transition-all hover:opacity-90"
          style={{ background: BRAND }}>
          📅 Schedule a Call
        </a>
      </Card>

      {/* Product overview */}
      <Card title="About Cordflex" subtitle="A quick overview while you wait">
        <ul className="space-y-2.5 text-sm text-gray-700">
          {[
            'ICU line management clip that organizes IV lines, tubes & cords',
            'Attaches to bed rails, IV poles, and patient gowns',
            'Used in ICU, CICU, MICU, Step-Down, and more',
            '203 product requests from 30 states',
            'Simple sell — nurses see the value immediately',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span style={{ color: GREEN }} className="flex-shrink-0">✓</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>

    {/* Contact */}
    <Card title="Questions?" subtitle="Reach us directly">
      <div className="flex flex-wrap gap-3">
        <a href="mailto:info@nursetechmedical.com"
          className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90"
          style={{ background: BRAND }}>
          ✉️ info@nursetechmedical.com
        </a>
        <a href={CALENDLY} target="_blank" rel="noreferrer"
          className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-90"
          style={{ background: LIGHT, color: BRAND }}>
          📅 Book a Call
        </a>
      </div>
    </Card>
  </div>
);

// ─────────────────────────────────────────────────────────────
// BASIC VIEW  (approved but not yet full partner)
// ─────────────────────────────────────────────────────────────
const BasicView = ({ profile }) => (
  <div className="space-y-6">
    {/* Upgrade banner */}
    <div className="rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
      style={{ background: LIGHT, border: `1px solid ${TEAL}` }}>
      <div className="flex-1">
        <p className="font-bold text-sm mb-1" style={{ color: BRAND }}>Welcome, {profile?.full_name}!</p>
        <p className="text-sm text-gray-600">
          You have basic access. Complete your onboarding call with David to unlock full training materials, the sales playbook, and sample requests.
        </p>
      </div>
      <a href={CALENDLY} target="_blank" rel="noreferrer"
        className="flex-shrink-0 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
        style={{ background: BRAND }}>
        📅 Schedule Onboarding Call
      </a>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Product overview */}
      <Card title="Product Overview">
        <div className="space-y-3 text-sm text-gray-700">
          <p><strong>Product:</strong> Cordflex Clip — ICU Line Management</p>
          <p><strong>Target:</strong> ICU, CICU, MICU, Step-Down, Progressive Care</p>
          <p><strong>Buyer:</strong> Charge Nurse, Nurse Manager, CNO, Supply Chain</p>
          <p><strong>Trial length:</strong> 2–4 weeks standard</p>
          <p><strong>Price point:</strong> Available after onboarding</p>
          <p><strong>Reorder:</strong> Consumable — monthly recurring</p>
        </div>
      </Card>

      {/* Schedule call */}
      <Card title="Complete Your Onboarding" subtitle="30 minutes, covers territory + training">
        <p className="text-sm text-gray-600 mb-4">
          This call covers: territory setup, pricing structure, how to introduce Cordflex to an account,
          and getting your first trial in place.
        </p>
        <a href={CALENDLY} target="_blank" rel="noreferrer"
          className="inline-block px-5 py-2.5 rounded-lg font-semibold text-sm text-white transition-all hover:opacity-90"
          style={{ background: GREEN }}>
          Book Your Onboarding Call →
        </a>
      </Card>
    </div>

    {/* Locked preview of full resources */}
    <Card title="Coming After Your Onboarding Call" subtitle="These resources unlock once David upgrades your access">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ResLink icon="🎥" label="MedOne Sales Training — Recording 1" locked />
        <ResLink icon="🎥" label="MedOne Sales Training — Recording 2" locked />
        <ResLink icon="📋" label="Full Sales Playbook (PDF)" locked />
        <ResLink icon="📄" label="Objection Handling Guide" locked />
        <ResLink icon="📦" label="Sample / Trial Request Form" locked />
        <ResLink icon="🔬" label="Clinical Evidence Package" locked />
      </div>
    </Card>

    {/* Support */}
    <Card title="Need Help?" subtitle="We're here">
      <div className="flex flex-wrap gap-3">
        <a href="mailto:info@nursetechmedical.com"
          className="px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ background: BRAND }}>
          ✉️ Email Us
        </a>
        <a href={CALENDLY} target="_blank" rel="noreferrer"
          className="px-4 py-2 rounded-lg text-sm font-medium"
          style={{ background: LIGHT, color: BRAND }}>
          📅 Schedule a Call
        </a>
      </div>
    </Card>
  </div>
);

// ─────────────────────────────────────────────────────────────
// FULL VIEW  (full partner access)
// ─────────────────────────────────────────────────────────────
const SampleForm = () => {
  const [form, setForm] = useState({ rep_name: '', hospital: '', unit_type: '', qty: '', notes: '' });
  const [sent, setSent] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        access_key: process.env.REACT_APP_WEB3FORMS_KEY,
        subject: `Distributor Sample Request — ${form.hospital}`,
        from_name: 'Cordflex Distributor Portal',
        ...form
      })
    });
    setSent(true);
  };

  if (sent) return (
    <div className="text-center py-4">
      <p className="text-green-700 font-semibold">✅ Sample request submitted! We'll be in touch within 1 business day.</p>
    </div>
  );

  const inputCls = "w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2";

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {[['rep_name','Your Name'],['hospital','Hospital / Facility'],['unit_type','Unit Type (e.g. ICU)'],['qty','# Units Requested']].map(([k,l]) => (
        <div key={k}>
          <label className="block text-xs font-semibold mb-1" style={{ color: BRAND }}>{l}</label>
          <input className={inputCls} value={form[k]} onChange={set(k)} required={k !== 'notes'} />
        </div>
      ))}
      <div className="sm:col-span-2">
        <label className="block text-xs font-semibold mb-1" style={{ color: BRAND }}>Notes</label>
        <textarea rows={3} className={inputCls} value={form.notes} onChange={set('notes')} placeholder="Trial timeline, contact at the hospital, etc." />
      </div>
      <div className="sm:col-span-2">
        <button type="submit" className="px-6 py-2.5 rounded-lg font-semibold text-sm text-white hover:opacity-90 transition-all"
          style={{ background: BRAND }}>
          Submit Request
        </button>
      </div>
    </form>
  );
};

const FullView = ({ profile }) => {
  const [openSection, setOpenSection] = useState('training');
  const sections = [
    { id: 'training', label: '🎥 Training', icon: '🎥' },
    { id: 'tools', label: '📋 Sales Tools', icon: '📋' },
    { id: 'evidence', label: '🔬 Clinical Evidence', icon: '🔬' },
    { id: 'samples', label: '📦 Request Samples', icon: '📦' },
    { id: 'support', label: '💬 Support', icon: '💬' },
  ];

  return (
    <div className="space-y-4">
      {/* Welcome */}
      <div className="rounded-xl p-5" style={{ background: BRAND, color: 'white' }}>
        <p className="font-bold text-lg">Welcome back, {profile?.full_name}. 👋</p>
        <p className="text-sm opacity-75 mt-1">{profile?.company} · Full Partner Access</p>
      </div>

      {/* Quick Start checklist */}
      <Card title="Quick Start Checklist" subtitle="Get your first rep sales-ready in under 45 minutes">
        <div className="space-y-2">
          {[
            ['1', 'Watch Training Recording 1 (20 min)', 'training'],
            ['2', 'Watch Training Recording 2 (15 min)', 'training'],
            ['3', 'Read the Sales Playbook — Quick Start section', 'tools'],
            ['4', 'Review the Objection Handling Guide', 'tools'],
            ['5', 'Request samples for your first target account', 'samples'],
            ['6', 'Join the partner WhatsApp group', 'support'],
          ].map(([num, text]) => (
            <div key={num} className="flex items-center gap-3 text-sm text-gray-700">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white"
                style={{ background: TEAL }}>{num}</span>
              <span>{text}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Tab nav */}
      <div className="flex gap-2 flex-wrap">
        {sections.map(s => (
          <button key={s.id} onClick={() => setOpenSection(s.id)}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{
              background: openSection === s.id ? BRAND : LIGHT,
              color: openSection === s.id ? 'white' : BRAND,
              border: `1px solid ${openSection === s.id ? BRAND : TEAL + '50'}`
            }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Training */}
      {openSection === 'training' && (
        <Card title="Training Recordings" subtitle="MedOne onboarding sessions — watch before your first sales call">
          <div className="space-y-5">
            <div>
              <p className="text-sm font-semibold mb-2" style={{ color: BRAND }}>Recording 1 — MedOne Onboarding</p>
              <div className="rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
                <iframe
                  src="https://drive.google.com/file/d/107dI2BGpX4oYE0RWPyWRfTsS7Xrt3mEy/preview"
                  width="100%" height="100%"
                  style={{ border: 0, display: 'block' }}
                  allow="autoplay"
                  title="Training Recording 1"
                />
              </div>
              <a href="https://drive.google.com/file/d/107dI2BGpX4oYE0RWPyWRfTsS7Xrt3mEy/view"
                target="_blank" rel="noreferrer"
                className="inline-block mt-2 text-xs font-medium hover:underline" style={{ color: GREEN }}>
                Open in Google Drive ↗
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold mb-2" style={{ color: BRAND }}>Recording 2 — MedOne Onboarding</p>
              <div className="rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
                <iframe
                  src="https://drive.google.com/file/d/1gS1RNTAXiPH1ecusG1J5CzOidd7zrN38/preview"
                  width="100%" height="100%"
                  style={{ border: 0, display: 'block' }}
                  allow="autoplay"
                  title="Training Recording 2"
                />
              </div>
              <a href="https://drive.google.com/file/d/1gS1RNTAXiPH1ecusG1J5CzOidd7zrN38/view"
                target="_blank" rel="noreferrer"
                className="inline-block mt-2 text-xs font-medium hover:underline" style={{ color: GREEN }}>
                Open in Google Drive ↗
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold mb-2" style={{ color: BRAND }}>Additional Recordings</p>
              <div className="space-y-2">
                <ResLink icon="🎥" label="Live Sales Call Recording — coming soon" locked={false}
                  href="#" />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Sales Tools */}
      {openSection === 'tools' && (
        <Card title="Sales Tools & Playbook">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ResLink icon="📋" label="Full Sales Playbook (PDF)" href="/resources/Sales Playbook — coming soon.pdf" locked={false} />
            <ResLink icon="📄" label="Cordflex Clip Flyer" href="/resources/Cordflex Clip Flyer.pdf" locked />
            <ResLink icon="📑" label="Technical Info Sheet" href="/resources/Cordflex-Technical Info.pdf" locked />
            <ResLink icon="🧾" label="Instructions for Use (PDF)" href="/resources/Cordflex-IFU.pdf" />
            <ResLink icon="💰" label="Cost-Benefit Analysis" href="/resources/Cost Benefit Analysis of Using Cordflex.pdf" locked />
            <ResLink icon="📊" label="Versatility Overview" href="/resources/Versatility of Cordflex.pdf" />
            <ResLink icon="🤝" label="Objection Handling Guide — coming soon" href="#" />
            <ResLink icon="📝" label="Sample Distributor Agreement — coming soon" href="#" />
          </div>
        </Card>
      )}

      {/* Clinical Evidence */}
      {openSection === 'evidence' && (
        <Card title="Clinical Evidence & Research">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ResLink icon="📚" label="Background Research Whitepaper" href="/resources/Cordflex Background Research Whitepaper (2).pdf" locked />
            <ResLink icon="💵" label="$1.7M Cost of Cord Disorganization" href="/resources/1.7M Cost of Cord Disorganization.pdf" locked />
            <ResLink icon="⚠️" label="Top 10 Health Tech Hazards 2025 (ecri.org)" href="https://home.ecri.org/blogs/ecri-thought-leadership-resources/techhazards2025" />
            <ResLink icon="🚶" label="Line Management & Falls Research" href="/resources/Line Management for Reducing Trips and Falls.pdf" locked />
            <ResLink icon="📈" label="Value Analysis Framework" href="/resources/Cordflex_Value_Analysis_Framework.pdf" locked />
            <ResLink icon="🏥" label="MedOne Partnership Overview" href="/resources/Cordflex-Med One.pdf" />
          </div>
        </Card>
      )}

      {/* Sample Request */}
      {openSection === 'samples' && (
        <Card title="Request Samples / Trial Units" subtitle="Submit a request and we'll follow up within 1 business day">
          <SampleForm />
        </Card>
      )}

      {/* Support */}
      {openSection === 'support' && (
        <Card title="Support & Contact">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Need help with an account, a deal, or a clinical question? Reach out directly.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href="mailto:info@nursetechmedical.com"
                className="flex items-center gap-3 p-4 rounded-xl text-sm font-medium transition-all hover:opacity-90"
                style={{ background: BRAND, color: 'white' }}>
                <span>✉️</span>
                <div>
                  <div className="font-bold">Email</div>
                  <div className="text-xs opacity-75">info@nursetechmedical.com</div>
                </div>
              </a>
              <a href={CALENDLY} target="_blank" rel="noreferrer"
                className="flex items-center gap-3 p-4 rounded-xl text-sm font-medium transition-all hover:opacity-90"
                style={{ background: LIGHT, color: BRAND, border: `1px solid ${TEAL}` }}>
                <span>📅</span>
                <div>
                  <div className="font-bold">Schedule a Call</div>
                  <div className="text-xs opacity-75">Book time with David</div>
                </div>
              </a>
              <a href="https://wa.me/1XXXXXXXXXX" target="_blank" rel="noreferrer"
                className="flex items-center gap-3 p-4 rounded-xl text-sm font-medium transition-all hover:opacity-90"
                style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>
                <span>💬</span>
                <div>
                  <div className="font-bold">WhatsApp Group</div>
                  <div className="text-xs opacity-75">Real-time deal support</div>
                </div>
              </a>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────
export default function DistributorPortalPage() {
  const navigate = useNavigate();
  const { user, profile, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: BRAND }}>
        <div className="text-white text-center">
          <div className="text-2xl mb-2">⏳</div>
          <p className="text-sm opacity-75">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    navigate('/distributors/login', { replace: true });
    return null;
  }

  const handleSignOut = async () => {
    await signOut();
    navigate('/distributors/login');
  };

  const level = profile.access_level;

  return (
    <Shell profile={profile} onSignOut={handleSignOut}>
      {level === 'pending' && <PendingView profile={profile} />}
      {level === 'basic'   && <BasicView   profile={profile} />}
      {(level === 'full' || level === 'admin') && <FullView profile={profile} />}
    </Shell>
  );
}
